import { useEffect } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { RealtimeStatus, useRealtime } from '@/context/RealtimeContext';
import { ReportType } from './ReportsPage/types';
import { reportKeys } from '@/services/api/reportsV2';

export interface ReportRealtimeEvent {
  type: 'report.created' | 'report.updated' | 'report.deleted' | 'report.revision.created' | 'report.attachment.changed';
  workspace_id: string;
  payload: { domain: 'reports'; reportType: ReportType; id: string };
}

const reportEvents = new Set(['report.created', 'report.updated', 'report.deleted', 'report.revision.created', 'report.attachment.changed']);

export const handleReportRealtime = (client: QueryClient, workspaceId: string, event: ReportRealtimeEvent): void => {
  if (!workspaceId || event.workspace_id !== workspaceId || !reportEvents.has(event.type) || event.payload?.domain !== 'reports' || !event.payload.id) return;
  void client.invalidateQueries({ queryKey: reportKeys.root(workspaceId), predicate: query => query.queryKey[2] === 'browse' || query.queryKey[2] === 'options' });
  if (event.type !== 'report.created') void client.invalidateQueries({ queryKey: reportKeys.detail(workspaceId, event.payload.reportType, event.payload.id) });
  if (event.type === 'report.revision.created') void client.invalidateQueries({ queryKey: reportKeys.revisions(workspaceId, event.payload.id) });
  if (event.type === 'report.attachment.changed') void client.invalidateQueries({ queryKey: reportKeys.attachments(workspaceId, event.payload.reportType, event.payload.id) });
};

export const useReportRealtime = (workspaceId: string): { status: RealtimeStatus } => {
  const client = useQueryClient(); const { subscribe, status } = useRealtime();
  useEffect(() => {
    if (!workspaceId) return undefined;
    return subscribe(event => handleReportRealtime(client, workspaceId, event as ReportRealtimeEvent));
  }, [client, subscribe, workspaceId]);
  return { status };
};
